import { useEffect } from 'react'
import type {
  ReviewDocument,
  DocType,
  DocStatus,
  Comment,
} from '../../data/mockDocuments'

// Re-export data types for convenience
export type { ReviewDocument, DocType, DocStatus, Comment }

// ── Column configuration ──────────────────────────────────────────────
export type ColumnId =
  | 'id'
  | 'date'
  | 'type'
  | 'sender'
  | 'subject'
  | 'summary'
  | 'status'
  | 'thread'
  | 'action'

export const TABLE_COLUMNS: {
  id: ColumnId
  label: string
  width: string
  required?: boolean
}[] = [
  { id: 'date', label: 'Date', width: '90px' },
  { id: 'type', label: 'Type', width: '90px' },
  { id: 'subject', label: 'Subject', width: '1fr', required: true },
  { id: 'summary', label: 'AI Summary', width: '1fr' },
  { id: 'sender', label: 'Sender', width: '140px' },
  { id: 'status', label: 'Status', width: '110px' },
  { id: 'thread', label: 'Thread', width: '44px' },
  { id: 'action', label: 'Preview', width: '50px' },
  { id: 'id', label: 'Bates #', width: '100px' },
]

export const ALL_COLUMN_IDS = TABLE_COLUMNS.map((c) => c.id)

// ── Sort / filter types ───────────────────────────────────────────────
export type SortColumn = 'id' | 'date' | 'type' | 'sender' | 'subject' | null
export type SortDirection = 'asc' | 'desc'

// ── Document review state ─────────────────────────────────────────────
export type Classification = 'hot' | 'critical' | 'tbd' | null

export interface DocReviewState {
  reviewed: boolean
  reviewedBy?: string
  reviewedAt?: string
  classification: Classification
  classifiedBy?: string
  privileged: boolean
  privilegedBy?: string
  privilegedAt?: string
}

export const DEFAULT_REVIEW_STATE: DocReviewState = {
  reviewed: false,
  classification: null,
  privileged: false,
}

// ── Case issues ───────────────────────────────────────────────────────
export interface CaseIssue {
  id: string
  label: string
  color: string
}

export const ISSUE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
]

// ── Family member type ────────────────────────────────────────────────
export interface FamilyMember {
  id: string
  name: string
  type: 'parent' | 'attachment'
  fileType?: string
  size?: string
}

// ── Display row type ──────────────────────────────────────────────────
export interface DisplayRow {
  doc: ReviewDocument
  isChild: boolean
  isTerminal: boolean
}

// ── AI types ──────────────────────────────────────────────────────
export interface AiMessage {
  id: string
  role: 'user' | 'ai'
  content: string
  timestamp: string
  resultType?: 'answer' | 'cohort'
  cohortDocIds?: string[]
}

export interface AiConversation {
  id: string
  title: string
  messages: AiMessage[]
  createdAt: string
  cohortDocIds?: string[]
}

// ── Saved cohort types ────────────────────────────────────────────────
export interface FilterSnapshot {
  typeFilters: string[]
  threadFilter: 'all' | 'terminal' | 'standalone'
  reviewFilter: 'all' | 'reviewed' | 'unreviewed'
  classificationFilter: 'all' | 'hot' | 'critical' | 'tbd' | 'unclassified'
  privilegedFilter: 'all' | 'privileged' | 'not-privileged'
  issueFilter: string | null
  participantSearch: string
  dateFrom: string
  dateTo: string
  groupByFamily: boolean
  hideDuplicates: boolean
}

export interface SavedCohort {
  id: string
  name: string
  filters: FilterSnapshot
  createdAt: string
  docCount: number
}

// ── Constants ─────────────────────────────────────────────────────────
export const ALL_TYPES: DocType[] = [
  'Email',
  'Contract',
  'Memo',
  'Invoice',
  'Letter',
  'Report',
]

export const DISPLAY_TYPES = [
  'Email - Business',
  'Email - Personal',
  'Legal Agreement',
  'Financial / Technical Report',
  'Internal Memo',
  'Invoice / Receipt',
  'Correspondence',
]

// ── Pure helper functions ─────────────────────────────────────────────
export function isBusinessEmail(email: string) {
  const domain = email.split('@')[1]?.toLowerCase()
  return [
    'acme.com',
    'lawfirm.com',
    'smithfirm.com',
    'kpmg.com',
    'techholdings.com',
    'globallogistics.com',
    'smithassociates.com',
    'defense.com',
    'nycourts.gov',
  ].includes(domain)
}

export function getTypeBadgeVariant(_type: DocType) {
  return 'grey' as const
}

export function getStatusBadgeVariant(status: DocStatus) {
  switch (status) {
    case 'Initial Review':
      return 'grey' as const
    case 'Enriched':
      return 'teal' as const
    case 'QA Flagged':
      return 'amber' as const
    case 'Privileged':
      return 'blue' as const
  }
}

// ── Thread helpers (take docs as param) ───────────────────────────────
export function getThreadParents(
  docs: ReviewDocument[],
): Map<string, ReviewDocument> {
  const parents = new Map<string, ReviewDocument>()
  for (const doc of docs) {
    if (doc.threadId && doc.threadPosition === 1) {
      parents.set(doc.threadId, doc)
    }
  }
  return parents
}

export function getThreadChildren(
  docs: ReviewDocument[],
  threadId: string,
): ReviewDocument[] {
  return docs
    .filter((d) => d.threadId === threadId && d.threadPosition !== 1)
    .sort((a, b) => (a.threadPosition ?? 0) - (b.threadPosition ?? 0))
}

// ── Click-outside hook ────────────────────────────────────────────────
export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void,
) {
  useEffect(() => {
    function listener(e: MouseEvent) {
      if (!ref.current || ref.current.contains(e.target as Node)) return
      handler()
    }
    document.addEventListener('mousedown', listener)
    return () => document.removeEventListener('mousedown', listener)
  }, [ref, handler])
}
